// Constants
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Controls
import { LazyPlanDetailsDialog } from "@ms/planner-presentational-controls/lib/local/planDetailsDialog/LazyPlanDetailsDialog";
import { PlanSettingsPanel } from "@ms/planner-presentational-controls/lib/local/planSettingsPanel/PlanSettingsPanel";
// Hooks
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { getContainerPermissionsFromStores, getPlanPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
import { ContainerType, SensitivityLabelAssignmentMethod } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import * as React from "react";
const componentName = "PlanSettingsPanelContainer";
/**
 * Container component for plan settings panel - handles all business logic and state management
 */ export const PlanSettingsPanelContainer = ({ planId, isOpen, onDismiss, onUpdatePlanTitle, onDeletePlan, elementToFocusOnDismiss, initialTab, onUpdatePlanBackground, fetchImageSuggestions, backgroundSuggestions })=>{
    const configProvider = useConfigProvider();
    const { traceLogger } = useLoggers();
    const [modernGroupsStore, planStore, appContextStore, tenantStore, permissionsStore, rosterStore, userStore, teamsChannelStore, driveItemStore, todoStore, dependencyStore, taskStore] = useStores([
        Store.ModernGroups,
        Store.Plan,
        Store.AppContext,
        Store.Tenant,
        Store.Permissions,
        Store.Roster,
        Store.User,
        Store.TeamsChannel,
        Store.DriveItem,
        Store.Todo,
        Store.Dependency,
        Store.Tasks
    ]);
    const [modernGroupActionCreator, containerActionCreator, planActionCreator] = useActionCreators([
        ActionCreator.ModernGroup,
        ActionCreator.Container,
        ActionCreator.Plan
    ]);
    const permissionUtilityStores = {
        permissionsStore,
        modernGroupsStore,
        rosterStore,
        userStore,
        teamsChannelStore,
        appContextStore,
        planStore,
        driveItemStore,
        todoStore,
        dependencyStore,
        taskStore
    };
    // State for group settings update
    const [isUpdatingGroupSettings, setIsUpdatingGroupSettings] = React.useState(false);
    const currentPlan = planStore.getPlan(planId).data ?? null;
    // Get plan container info
    const planContainer = currentPlan?.container;
    const planContainerId = planContainer?.containerId;
    const planContainerType = planContainer?.containerType ?? null;
    // Get modern group if this is a group plan
    const modernGroup = planContainerType === ContainerType.Group && planContainerId != null ? modernGroupsStore.getModernGroup(planContainerId)?.data ?? undefined : undefined;
    // Get planner roster if this is a roster plan
    const plannerRoster = planContainerType === ContainerType.Roster && planContainerId != null ? rosterStore.getRoster(planContainerId)?.data ?? null : null;
    // Get current user and owners
    const { userId } = appContextStore.getAppContext();
    const currentUserId = userId ?? null;
    const ownersResult = modernGroup ? modernGroupsStore.getModernGroupOwners(modernGroup.id) : null;
    let isGroupOwner = false;
    if (planContainerType === ContainerType.Group) {
        isGroupOwner = currentUserId != null && currentUserId.length > 0 && ownersResult != null && ownersResult.status.state === StoreEntityState.Present ? ownersResult.data.some((ownerId)=>ownerId === currentUserId) : undefined;
    }
    // Fetch owners when panel opens (lazy loading - only when needed)
    React.useEffect(()=>{
        if (!isOpen || !modernGroup?.id) {
            return;
        }
        modernGroupActionCreator.fetchModernGroupOwners(modernGroup.id);
    }, [
        isOpen,
        modernGroup?.id,
        modernGroupActionCreator
    ]);
    // Fetch background image suggestions when the EVO dialog opens. The legacy PlanSettings panel
    // owns its own fetch trigger internally, so we only kick this off here when the new dialog
    // path is active to avoid double-fetching.
    const isEvoDialogActive = configProvider().flights.EnableEvoCalendar;
    const planDisplayName = currentPlan?.displayName ?? "";
    React.useEffect(()=>{
        if (!isOpen || !isEvoDialogActive || !fetchImageSuggestions || !planId) {
            return;
        }
        fetchImageSuggestions(planId, planDisplayName);
    }, [
        isOpen,
        isEvoDialogActive,
        fetchImageSuggestions,
        planId,
        planDisplayName
    ]);
    /**
     * Callback when a sensitivity label is selected (for Roster container type)
     */ const onSensitivitySelected = React.useCallback(async (selectedLabel)=>{
        if (!plannerRoster) {
            traceLogger.logTrace(0x1e0492c1 /* tag_4bjlb */ , TraceLevel.Warning, `${componentName}: Cannot update roster sensitivity label: plannerRoster not available`);
            return;
        }
        traceLogger.logTrace(0x1e0492c0 /* tag_4bjla */ , TraceLevel.Info, `${componentName}: Updating roster sensitivity label to: ${selectedLabel.id} [RosterId=${plannerRoster.id}][Version=${plannerRoster.version}]`);
        // Create updated roster with new sensitivity label
        const updatedRoster = plannerRoster.setProperty("assignedSensitivityLabel", {
            sensitivityLabelId: selectedLabel.id,
            assignmentMethod: SensitivityLabelAssignmentMethod.Privileged
        });
        await containerActionCreator.setRosterSensitivityLabelAsync(updatedRoster);
    }, [
        plannerRoster,
        containerActionCreator,
        traceLogger
    ]);
    /**
     * Callback when group settings are saved
     */ const onUpdateGroupSettings = React.useCallback(async (updatedGroup)=>{
        if (!modernGroup) {
            traceLogger.logTrace(0x1e10f79a /* tag_4ep40 */ , TraceLevel.Warning, `${componentName}: Cannot update group settings: modernGroup not available`);
            return;
        }
        try {
            setIsUpdatingGroupSettings(true);
            // Use proper model update pattern with setProperty
            let updatedModernGroup = modernGroup;
            updatedModernGroup = updatedModernGroup.setProperty("displayName", updatedGroup.displayName);
            // Don't set description if it's empty or whitespace-only to avoid API errors
            if (updatedGroup.description.trim() !== "") {
                updatedModernGroup = updatedModernGroup.setProperty("description", updatedGroup.description);
            }
            updatedModernGroup = updatedModernGroup.setProperty("visibility", updatedGroup.visibility);
            updatedModernGroup = updatedModernGroup.setProperty("assignedSensitivityLabelId", updatedGroup.assignedSensitivityLabelId);
            await modernGroupActionCreator.updateModernGroup(modernGroup, updatedModernGroup);
            traceLogger.logTrace(0x1e10f799 /* tag_4ep4z */ , TraceLevel.Info, `${componentName}: Group settings updated successfully`);
        } catch (error) {
            traceLogger.logTrace(0x1e10f798 /* tag_4ep4y */ , TraceLevel.Error, `${componentName}: Failed to update group settings: ${error}`);
        } finally{
            setIsUpdatingGroupSettings(false);
        }
    }, [
        modernGroup,
        modernGroupActionCreator,
        traceLogger
    ]);
    /**
     * Callback when the dialog's Scheduling tab edits working hours or plan time zone.
     */ const onCalendarChange = React.useCallback((next)=>{
        if (!currentPlan) {
            return;
        }
        const updatedPlan = currentPlan.setProperty("calendar", next);
        planActionCreator.updatePlan(currentPlan, updatedPlan);
    }, [
        currentPlan,
        planActionCreator
    ]);
    if (!currentPlan) {
        return null;
    }
    const isTabView = configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.Tab;
    const planPermissions = getPlanPermissionsFromStores(currentPlan, permissionUtilityStores, traceLogger);
    const containerPermissions = getContainerPermissionsFromStores(planContainer ?? null, permissionUtilityStores, traceLogger);
    if (configProvider().flights.EnableEvoCalendar) {
        // The dialog is mount-gated on isOpen so the lazy chunk only loads when the user actually
        // opens plan settings, and the dialog gets a fresh mount each time (no need for internal
        // open-state plumbing). Suspense fallback is null - the chunk is small and a flashing
        // spinner would be worse UX than nothing; the Fluent modal backdrop is its own load ack.
        return isOpen ? /*#__PURE__*/ React.createElement(React.Suspense, {
            fallback: null
        }, /*#__PURE__*/ React.createElement(LazyPlanDetailsDialog, {
            plan: currentPlan,
            modernGroup: modernGroup,
            plannerRoster: plannerRoster ?? undefined,
            canRenamePlan: planPermissions.canUpdateDisplayName,
            canUpdateVisuals: planPermissions.canUpdateVisuals,
            canUpdateCalendar: planPermissions.canUpdateCalendar,
            canDeletePlan: planPermissions.canDelete,
            isGroupOwner: isGroupOwner,
            isTabView: isTabView,
            canChangeSensitivity: containerPermissions.canChangeSensitivity,
            enableMIPLabels: tenantStore.getCurrentTenantGroupSettings()?.data?.enableMIPLabels ?? false,
            hasSharedContainers: currentPlan.sharedWithContainers?.length > 0,
            sensitivityLabels: tenantStore.getCurrentSensitivityLabels().data ?? [],
            userAccessibleTenantSensitivityLabels: tenantStore.getCurrentSensitivityLabels().data ?? [],
            currentSensitivityLabelId: // Roster-backed plans carry sensitivity on the roster itself; group-backed
            // plans carry it on the modern group. Reading the wrong source here yields
            // an empty label id and the SharingAndSecurityPage shows no current label.
            planContainerType === ContainerType.Roster ? plannerRoster?.assignedSensitivityLabel?.sensitivityLabelId ?? "" : modernGroup?.assignedSensitivityLabelId ?? "",
            backgroundSuggestions: backgroundSuggestions,
            isUpdatingGroupSettings: isUpdatingGroupSettings,
            initialTab: initialTab,
            onDismiss: onDismiss,
            onUpdatePlanTitle: onUpdatePlanTitle,
            onDeletePlan: onDeletePlan,
            onUpdatePlanBackground: onUpdatePlanBackground,
            onCalendarChange: onCalendarChange,
            onUpdateGroupSettings: onUpdateGroupSettings,
            onSensitivitySelected: onSensitivitySelected
        })) : null;
    }
    return /*#__PURE__*/ React.createElement(PlanSettingsPanel, {
        plan: currentPlan,
        modernGroup: modernGroup,
        isOpen: isOpen,
        canDeletePlan: planPermissions.canDelete,
        isTabView: isTabView,
        isGroupOwner: isGroupOwner,
        canChangeSensitivity: containerPermissions.canChangeSensitivity,
        onUpdatePlanTitle: onUpdatePlanTitle,
        canRenamePlan: planPermissions.canUpdateDisplayName,
        canUpdateVisuals: planPermissions.canUpdateVisuals,
        onDeletePlan: onDeletePlan,
        onDismiss: onDismiss,
        elementToFocusOnDismiss: elementToFocusOnDismiss,
        targetElementId: ViewElementIds.BasicPlanView,
        backgroundSuggestions: backgroundSuggestions,
        onUpdatePlanBackground: onUpdatePlanBackground,
        fetchImageSuggestions: fetchImageSuggestions,
        areBackgroundSuggestionsEnabled: !configProvider().settings.disableImageRecommendations,
        sensitivityLabels: tenantStore.getCurrentSensitivityLabels().data ?? [],
        currentSensitivityLabelId: modernGroup?.assignedSensitivityLabelId || "",
        onUpdateGroupSettings: onUpdateGroupSettings,
        isUpdatingGroupSettings: isUpdatingGroupSettings,
        plannerRoster: plannerRoster ?? undefined,
        hasSharedContainers: currentPlan.sharedWithContainers?.length > 0,
        enableMIPLabels: tenantStore.getCurrentTenantGroupSettings()?.data?.enableMIPLabels ?? false,
        userAccessibleTenantSensitivityLabels: tenantStore.getCurrentSensitivityLabels().data ?? [],
        onSensitivitySelected: onSensitivitySelected
    });
};
